import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Search, Eye, Printer, FileText, Calendar, User } from 'lucide-react';
import { prescriptionService } from '../../consultation/api/prescriptions';
import { patientService } from '../../../lib/services';
import { useStore } from '../../../lib/store';
import { getDoctorIdFromUser, isDoctor } from '../../../lib/rbac';
import toast from 'react-hot-toast';
import type { Prescription } from '../../consultation/types';
import type { Patient } from '../../../lib/supabase';

const PrescriptionList: React.FC = () => {
  const { user } = useStore();
  const [prescriptions, setPrescriptions] = useState<Prescription[]>([]);
  const [patients, setPatients] = useState<Record<string, Patient>>({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('');

  const doctorId = getDoctorIdFromUser(user);

  useEffect(() => {
    if (!isDoctor(user)) {
      toast.error('Access denied. Doctor privileges required.');
      return;
    }
    
    if (!doctorId) {
      toast.error('Doctor ID not found. Please contact administrator.');
      return;
    }

    loadPrescriptions();
  }, [user, doctorId]);

  const loadPrescriptions = async () => {
    if (!doctorId) return;
    
    try {
      setLoading(true);
      const prescriptionsData = await prescriptionService.getByDoctor(doctorId);
      setPrescriptions(prescriptionsData);

      const patientIds = [...new Set(prescriptionsData.map(p => p.patientId).filter(Boolean))] as string[];
      const patientsData: Record<string, Patient> = {};
      
      for (const patientId of patientIds) {
        try {
          const patient = await patientService.getById(patientId);
          patientsData[patientId] = patient;
        } catch (error) {
          console.error(`Failed to load patient ${patientId}:`, error);
        }
      }
      
      setPatients(patientsData);
    } catch (error) {
      console.error('Failed to load prescriptions:', error);
      toast.error('Failed to load prescriptions');
    } finally {
      setLoading(false);
    }
  };

  const filteredPrescriptions = prescriptions.filter(prescription => {
    const patient = patients[prescription.patientId];
    const matchesSearch = !searchTerm || 
      patient?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      prescription.id.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesDate = !dateFilter || 
      prescription.createdAt?.startsWith(dateFilter);
    
    return matchesSearch && matchesDate;
  });

  if (!isDoctor(user)) {
    return (
      <div className="text-center py-12">
        <div className="text-red-500">Access denied. Doctor privileges required.</div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Prescriptions</h1>
          <p className="text-gray-600">Manage your patient prescriptions</p>
        </div>
        <Link
          to="/doctor/prescriptions/new"
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center space-x-2"
        >
          <Plus className="w-4 h-4" />
          <span>New Prescription</span>
        </Link>
      </div>

      <div className="bg-white p-4 rounded-lg shadow-sm border">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search by patient name or prescription ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full"
            />
          </div>

          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="date"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full"
            />
          </div>

          <button
            onClick={() => {
              setSearchTerm('');
              setDateFilter('');
            }}
            className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Clear Filters
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Patient
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Medications
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredPrescriptions.map((prescription) => {
                const patient = patients[prescription.patientId];
                return (
                  <tr key={prescription.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <User className="w-4 h-4 text-gray-400 mr-2" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {patient?.name || 'Unknown Patient'}
                          </div>
                          <div className="text-sm text-gray-500">
                            {patient?.email || 'No email'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {prescription.createdAt ? new Date(prescription.createdAt).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {prescription.items.slice(0, 2).map((item, index) => (
                          <div key={index} className="truncate">
                            {item.nameOrMedicineId}
                          </div>
                        ))}
                        {prescription.items.length > 2 && (
                          <div className="text-xs text-gray-500">
                            +{prescription.items.length - 2} more
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                        Active
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <Link
                          to={`/doctor/prescriptions/${prescription.id}`}
                          className="text-blue-600 hover:text-blue-900"
                          title="View Prescription"
                        >
                          <Eye className="w-4 h-4" />
                        </Link>
                        <Link
                          to={`/prescriptions/${prescription.id}/print`}
                          className="text-green-600 hover:text-green-900"
                          title="Print Prescription"
                        >
                          <Printer className="w-4 h-4" />
                        </Link>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {filteredPrescriptions.length === 0 && (
          <div className="text-center py-12">
            <FileText className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No prescriptions found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {prescriptions.length === 0 
                ? "You haven't created any prescriptions yet."
                : "No prescriptions match your current filters."
              }
            </p>
            <div className="mt-6">
              <Link
                to="/doctor/prescriptions/new"
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create New Prescription
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PrescriptionList;
